/**
 * EIFormerEmployees — analytics over leavers.
 *
 * Three layers:
 *   1. Headline KPIs (good vs bad vs unknown leavers)
 *   2. Retention Fit Model — live validation showing how well the model
 *      separates good from bad on the actual data (the user can see if
 *      the correlation is real)
 *   3. AI-generated insight (deltas, narrative)
 *   4. Side-by-side DISC averages + primary-style distribution
 *   5. The leaver directory (re-uses EIEmployees with employment_status="good_leaver" | "bad_leaver")
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  UserMinus, UserX, HelpCircle, Loader2, Sparkles, RefreshCw, Gauge,
} from "lucide-react";
import { API, DISC_COLOURS } from "../EmploymentIntelligence";
import EIEmployees from "./EIEmployees";
import RetentionFitBadge from "./RetentionFitBadge";

export default function EIFormerEmployees({ onOpenProfile }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [view, setView] = useState("good_leaver");
  const [model, setModel] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        axios.get(`${API}/employment-intelligence/former-analytics`),
        axios.get(`${API}/employment-intelligence/retention-model`),
      ]);
      setData(a.data);
      setModel(m.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const runInsight = useCallback(async () => {
    setInsightLoading(true);
    setInsight(null);
    try {
      const res = await axios.get(`${API}/employment-intelligence/former-insight`);
      setInsight(res.data.insight);
    } catch {
      setInsight("Could not generate the insight right now — try again in a moment.");
    } finally {
      setInsightLoading(false);
    }
  }, []);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center", color: "#6a707d" }}><Loader2 size={20} className="ei-spin" /></div>;
  }

  const good = data?.good_count || 0;
  const bad = data?.bad_count || 0;
  const unknown = data?.unknown_count || 0;

  const ga = data?.good_average || {};
  const ba = data?.bad_average || {};
  const deltas = data?.deltas || {};

  const renderAvgBar = (key, value, peer, color) => {
    if (value === null || value === undefined) {
      return <div className="ei-avg-row" key={key}><span className="ei-avg-letter">{key}</span><span style={{ color: "#9ba3b1" }}>—</span></div>;
    }
    const pct = Math.max(0, Math.min(100, value));
    const delta = peer != null ? Math.round(value - peer) : null;
    return (
      <div className="ei-avg-row" key={key}>
        <span className="ei-avg-letter">{key}</span>
        <div className="ei-avg-track">
          <div className="ei-avg-fill" style={{ width: `${pct}%`, background: color }} />
        </div>
        <span className="ei-avg-value">{Math.round(value)}</span>
        {delta !== null && (
          <span className="ei-avg-delta" style={{ color: delta > 0 ? "#0ea5e9" : delta < 0 ? "#e11d48" : "#6a707d" }}>
            {delta > 0 ? "+" : ""}{delta}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="ei-section-head">
        <div>
          <h2>Former employees</h2>
          <p>Patterns and DISC averages across the people who&apos;ve left — Good vs Bad leavers, what they had in common, and what it means for hiring.</p>
        </div>
        <button className="ei-secondary" onClick={loadAnalytics}><RefreshCw size={13} /> Refresh</button>
      </div>

      {/* KPI strip */}
      <div className="ei-former-kpis">
        <div className="ei-former-kpi" style={{ "--accent": "#0ea5e9" }}>
          <UserMinus size={18} />
          <div>
            <strong>{good}</strong>
            <span>Good leavers</span>
          </div>
        </div>
        <div className="ei-former-kpi" style={{ "--accent": "#e11d48" }}>
          <UserX size={18} />
          <div>
            <strong>{bad}</strong>
            <span>Bad leavers</span>
          </div>
        </div>
        <div className="ei-former-kpi" style={{ "--accent": "#6a707d" }}>
          <HelpCircle size={18} />
          <div>
            <strong>{unknown}</strong>
            <span>Untagged leavers</span>
          </div>
        </div>
      </div>

      {/* Retention Fit Model — validation card */}
      {model && (
        <section className="ei-card ei-model-card" style={{ marginTop: 18 }}>
          <div className="ei-card-head">
            <h3 className="ei-card-title"><Gauge size={14} /> Retention Fit model</h3>
            <span className="ei-card-sub">
              {model.ready
                ? `Trained on ${model.model.good_n} good · ${model.model.bad_n} bad leavers`
                : "Model not ready yet"}
            </span>
          </div>
          {!model.ready ? (
            <p style={{ color: "#6a707d", margin: 0, fontSize: 13.5 }}>{model.reason}</p>
          ) : (
            <>
              <p className="ei-model-blurb">
                Every employee gets a 0-100% score against your live good vs bad leaver patterns.
                <strong> Green ≥ {model.thresholds.green}%</strong>, amber in the middle, <strong>red &lt; {model.thresholds.red}%</strong>.
                The bigger the gap below between good and bad averages, the stronger the signal.
              </p>

              {/* Validation strip — does the model actually separate good from bad? */}
              <div className="ei-model-validation">
                <div className="ei-model-row">
                  <div className="ei-model-row-label" style={{ color: "#0ea5e9" }}>
                    <UserMinus size={13} /> Good leavers
                    <span>n={model.validation.good_n}</span>
                  </div>
                  <div className="ei-model-row-bar">
                    <div className="ei-model-track">
                      <div className="ei-model-fill" style={{ width: `${model.validation.good_average}%`, background: "#0ea5e9" }} />
                    </div>
                  </div>
                  <RetentionFitBadge
                    fit={{ score: model.validation.good_average, band: model.validation.good_average >= model.thresholds.green ? "green" : (model.validation.good_average < model.thresholds.red ? "red" : "amber"), contributions: {} }}
                    size="lg"
                    title={`Average retention fit of all Good Leavers (n=${model.validation.good_n})`}
                  />
                </div>
                <div className="ei-model-row">
                  <div className="ei-model-row-label" style={{ color: "#e11d48" }}>
                    <UserX size={13} /> Bad leavers
                    <span>n={model.validation.bad_n}</span>
                  </div>
                  <div className="ei-model-row-bar">
                    <div className="ei-model-track">
                      <div className="ei-model-fill" style={{ width: `${model.validation.bad_average}%`, background: "#e11d48" }} />
                    </div>
                  </div>
                  <RetentionFitBadge
                    fit={{ score: model.validation.bad_average, band: model.validation.bad_average >= model.thresholds.green ? "green" : (model.validation.bad_average < model.thresholds.red ? "red" : "amber"), contributions: {} }}
                    size="lg"
                    title={`Average retention fit of all Bad Leavers (n=${model.validation.bad_n})`}
                  />
                </div>
                {model.validation.active_n > 0 && (
                  <div className="ei-model-row">
                    <div className="ei-model-row-label" style={{ color: "#10b981" }}>
                      <HelpCircle size={13} /> Active employees
                      <span>n={model.validation.active_n}</span>
                    </div>
                    <div className="ei-model-row-bar">
                      <div className="ei-model-track">
                        <div className="ei-model-fill" style={{ width: `${model.validation.active_average}%`, background: "#10b981" }} />
                      </div>
                    </div>
                    <RetentionFitBadge
                      fit={{ score: model.validation.active_average, band: model.validation.active_average >= model.thresholds.green ? "green" : (model.validation.active_average < model.thresholds.red ? "red" : "amber"), contributions: {} }}
                      size="lg"
                      title={`Average retention fit of currently Active employees (n=${model.validation.active_n})`}
                    />
                  </div>
                )}
              </div>

              <div className="ei-model-buckets">
                <div>
                  <div className="ei-model-buckets-title" style={{ color: "#0ea5e9" }}>Good leaver buckets</div>
                  <div className="ei-model-bucket-row">
                    <span className="ei-fit-badge ei-fit-badge-green">{model.validation.good_buckets.green}</span>
                    <span className="ei-fit-badge ei-fit-badge-amber">{model.validation.good_buckets.amber}</span>
                    <span className="ei-fit-badge ei-fit-badge-red">{model.validation.good_buckets.red}</span>
                  </div>
                </div>
                <div>
                  <div className="ei-model-buckets-title" style={{ color: "#e11d48" }}>Bad leaver buckets</div>
                  <div className="ei-model-bucket-row">
                    <span className="ei-fit-badge ei-fit-badge-green">{model.validation.bad_buckets.green}</span>
                    <span className="ei-fit-badge ei-fit-badge-amber">{model.validation.bad_buckets.amber}</span>
                    <span className="ei-fit-badge ei-fit-badge-red">{model.validation.bad_buckets.red}</span>
                  </div>
                </div>
              </div>

              <p className="ei-model-foot">
                <strong>Separation: {model.validation.separation != null ? `${model.validation.separation > 0 ? "+" : ""}${model.validation.separation}` : "—"} points</strong>
                {" — "}
                {model.validation.separation == null
                  ? "Need more data."
                  : model.validation.separation >= 15
                    ? "Strong predictive signal."
                    : model.validation.separation >= 5
                      ? "Mild predictive signal — will sharpen as more leavers are added."
                      : "Weak signal — the two groups overlap heavily on DISC alone."}
              </p>
            </>
          )}
        </section>
      )}

      {/* AI insight */}
      <section className="ei-card ei-insight-card" style={{ marginTop: 18 }}>
        <div className="ei-card-head">
          <h3 className="ei-card-title"><Sparkles size={14} /> AI pattern analysis</h3>
          <button className="ei-primary" onClick={runInsight} disabled={insightLoading || (good + bad < 4)} data-testid="ei-run-insight">
            {insightLoading ? <Loader2 size={13} className="ei-spin" /> : <Sparkles size={13} />}
            {insight ? "Re-analyse" : "Generate insight"}
          </button>
        </div>
        {(good + bad < 4) ? (
          <p style={{ color: "#6a707d", margin: 0, fontSize: 13.5 }}>
            Tag at least 4 former employees (Good or Bad) and AI will compare their behavioural patterns and call out the biggest differentiators.
          </p>
        ) : insightLoading ? (
          <p style={{ color: "#6a707d", margin: 0 }}>Analysing patterns across {good} good vs {bad} bad leavers…</p>
        ) : insight ? (
          <p className="ei-insight-text">{insight}</p>
        ) : (
          <p style={{ color: "#6a707d", margin: 0, fontSize: 13.5 }}>
            Click &ldquo;Generate insight&rdquo; — AI will read the DISC averages, blend frequencies and primary-style distributions across {good} good leavers and {bad} bad leavers, and write 4-6 sentences explaining what&apos;s behaviourally different between them.
          </p>
        )}
      </section>

      {/* Side-by-side averages */}
      {(good > 0 || bad > 0) && (
        <section className="ei-card" style={{ marginTop: 18 }}>
          <div className="ei-card-head">
            <h3 className="ei-card-title">Average DISC — Good vs Bad leavers</h3>
            <span className="ei-card-sub">Δ shown is Good minus Bad</span>
          </div>
          <div className="ei-avg-cols">
            <div>
              <div className="ei-avg-col-title" style={{ color: "#0ea5e9" }}>
                <UserMinus size={14} /> Good leavers <span>n = {good}</span>
              </div>
              {["D", "I", "S", "C"].map((k) => {
                const key = { D: "D", I: "I", S: "S", C: "C" }[k];
                return renderAvgBar(key, ga[key], ba[key], DISC_COLOURS[key].fill);
              })}
            </div>
            <div>
              <div className="ei-avg-col-title" style={{ color: "#e11d48" }}>
                <UserX size={14} /> Bad leavers <span>n = {bad}</span>
              </div>
              {["D", "I", "S", "C"].map((k) => {
                return renderAvgBar(k, ba[k], ga[k], DISC_COLOURS[k].fill);
              })}
            </div>
          </div>
        </section>
      )}

      {/* Style distribution */}
      {(good > 0 || bad > 0) && (
        <section className="ei-card" style={{ marginTop: 18 }}>
          <div className="ei-card-head">
            <h3 className="ei-card-title">Primary style distribution</h3>
          </div>
          <div className="ei-style-cmp">
            {["D", "I", "S", "C"].map((letter) => {
              const gv = (data?.good_style_distribution?.[letter] || 0);
              const bv = (data?.bad_style_distribution?.[letter] || 0);
              const max = Math.max(gv, bv, 1);
              return (
                <div key={letter} className="ei-style-cmp-row">
                  <span className={`ei-disc-chip ei-disc-chip-${letter}`}>{letter}</span>
                  <div>
                    <div className="ei-style-cmp-name">Good</div>
                    <div className="ei-avg-track">
                      <div className="ei-avg-fill" style={{ width: `${(gv / max) * 100}%`, background: "#0ea5e9" }} />
                    </div>
                    <span className="ei-style-cmp-num">{gv}</span>
                  </div>
                  <div>
                    <div className="ei-style-cmp-name">Bad</div>
                    <div className="ei-avg-track">
                      <div className="ei-avg-fill" style={{ width: `${(bv / max) * 100}%`, background: "#e11d48" }} />
                    </div>
                    <span className="ei-style-cmp-num">{bv}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Leaver directory split by good/bad */}
      <section className="ei-card" style={{ marginTop: 22, padding: "18px 22px 6px" }}>
        <div className="ei-tabs-inner" style={{ marginBottom: 10 }}>
          <button
            className={`ei-tab ${view === "good_leaver" ? "is-active" : ""}`}
            onClick={() => setView("good_leaver")}
            data-testid="ei-former-tab-good"
          >
            <UserMinus size={13} /> Good leavers
          </button>
          <button
            className={`ei-tab ${view === "bad_leaver" ? "is-active" : ""}`}
            onClick={() => setView("bad_leaver")}
            data-testid="ei-former-tab-bad"
          >
            <UserX size={13} /> Bad leavers
          </button>
        </div>
        <EIEmployees
          key={view}
          onOpenProfile={onOpenProfile}
          initialStatus={view}
          title={view === "good_leaver" ? "Good leavers" : "Bad leavers"}
          blurb={view === "good_leaver"
            ? "People who left in good standing — useful as positive benchmarks for hiring."
            : "People who left under poor circumstances — patterns here drive your retention model."}
        />
      </section>
    </>
  );
}
